'use client';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import type { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { GdprTextNewsletter } from '@eliadmin/core/src/utils/gdpr-tos/gdpr-text-newsletter';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';
import { LuSend } from 'react-icons/lu';

export function Newsletter() {
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: async (input: Parameters<typeof newsletterSubscribe>[0]) => {
      const { newsletterSubscribe } =
        await import('@eliadmin/core/src/api/newsletter/newsletter-api');
      return newsletterSubscribe(input);
    },
    onSuccess: () => setIsSubscribed(true),
  });

  const locale = useLocaleStore(s => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <form
      className={cn(
        'flex w-full flex-col items-start justify-start',
        'ea-homepage-newsletter__form',
      )}
      onSubmit={handleSubmit}
    >
      <div
        className={cn(
          'flex w-full flex-col justify-center space-y-3',
          'ea-homepage-newsletter__form-item-email',
        )}
      >
        <div className={cn('flex flex-row items-center gap-2 sm:gap-3 lg:gap-4')}>
          <Input
            aria-label={systemLabels('email')}
            id="newsletter-email"
            type="email"
            name="email"
            placeholder={t('text183')}
            onChange={e => setEmail(e.target.value)}
            className={{
              wrapper: cn(
                'h-12 !rounded-none border-none bg-white text-muted-foreground sm:h-14',
                'ea-homepage-newsletter__form-item-email-input',
              ),
              main: cn('h-12 !rounded-none sm:h-14'),
            }}
          />
          <Button
            type="submit"
            variant={'dark'}
            className={cn(
              'hidden border-none sm:flex',
              'ea-homepage-newsletter__form-item-email-submit-button',
            )}
            disabled={email.length === 0 || !checked || isSubscribed}
            isLoading={subscribeMutation.isPending}
          >
            {t('text182')}
          </Button>
          <Button
            type="submit"
            variant={'dark'}
            aria-label={t('text182')}
            className={cn(
              'flex min-w-12 border-none px-0 sm:hidden',
              'ea-homepage-newsletter__form-item-email-submit-button',
            )}
            disabled={email.length === 0 || !checked || isSubscribed}
            isLoading={subscribeMutation.isPending}
          >
            <LuSend size={20} />
          </Button>
        </div>
        {isSubscribed && <span className={cn('font-semibold text-success')}>{t('text234')}</span>}
        <div
          className={cn(
            'flex flex-row items-center space-x-2',
            'ea-homepage-newsletter__form-item-gdpr',
          )}
        >
          <Checkbox
            checked={checked}
            onCheckedChange={() => setChecked(!checked)}
            id="newsletter-gdpr"
            aria-label={t('text264') + ' ' + t('text265') + ' ' + t('text436')}
            className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox')}
            required
          />
          <Label
            htmlFor="newsletter-gdpr"
            className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox-label')}
          >
            <p className="text-xs tracking-tight text-black/70">
              {t('text2002')}{' '}
              <Link prefetch={false} href={'/ochrana-osobnych-udajov'} className="underline">
                {t('text614')}.
              </Link>
            </p>
          </Label>
        </div>
      </div>
    </form>
  );
}
